import React, { lazy } from 'react';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import { Stack } from 'new/elements/Stack';
import { Table } from 'new/elements/Table';
import Typography from 'new/elements/Typography';
import { UseFlowRunsTableReturnedType } from 'new/components/monitor/Runs/hooks/useFlowRuns';
import { useDataFlowStatusSelector } from 'new/hooks/useDataFlowStatusSelector';
import { generateColumnsForFlowRunsTable } from 'new/components/monitor/Runs/components/FlowRunsTable/generateColumnsForFlowRunsTable';
import { getFlowHealthSeverity } from 'new/elements/FlowHealthBar';
import { FlowRun } from 'new/components/monitor/Runs/components/FlowRunsTable/types';

import s from './FlowRunsTable.module.scss';

const FlowDetailViewContainer = lazy(
  () => import('js/components/data_flows/cards/FlowDetailViewContainer'),
);
const FlowDetailViewContainerLazy = (props: any) => {
  return (
    <React.Suspense fallback={null}>
      <FlowDetailViewContainer {...props} />
    </React.Suspense>
  );
};

const PreviewFlow = lazy(
  () => import('new/components/monitor/Runs/components/PreviewFlow/PreviewFlow'),
);

const PreviewFlowLazy = (props: any) => {
  return (
    <React.Suspense fallback={null}>
      <PreviewFlow {...props} />
    </React.Suspense>
  );
};

type Props = {
  generateColumns: typeof generateColumnsForFlowRunsTable;
  parentScrollRef: React.RefObject<HTMLDivElement>;
  /** Triage chat (owned by FlowRunsScreen so the modal survives this table's
   *  data-driven re-renders / empty-state early return). */
  triageChatEnabled?: boolean;
  onOpenTriage?: (row: any) => void;
} & Pick<UseFlowRunsTableReturnedType['0'], 'data' | 'loading' | 'error'>;

function FlowRunsTable(props: Props) {
  const history = useHistory();
  const { selectedCardID } = useDataFlowStatusSelector();
  const { data, error, loading, triageChatEnabled, onOpenTriage } = props;

  const getRowClassName = React.useCallback((row: FlowRun) => {
    const hasErrors = getFlowHealthSeverity(row?.metrics) === 'error';
    return cx(s.rowClassName, { [s.rowClassNameError]: hasErrors });
  }, []);

  const columns = React.useMemo(
    () =>
      props?.generateColumns({
        history,
        triageChatEnabled,
        onOpenTriage,
      }),
    [props.generateColumns, history, triageChatEnabled, onOpenTriage],
  );

  if (error) {
    console.error('Error fetching file item details: FileItemDetails');
    return null;
  }

  if (data?.length === 0 && !loading)
    return (
      <Stack p={16} alignItems="center" justifyContent="center">
        <Typography>Runs history is not available</Typography>
      </Stack>
    );

  return (
    <>
      <Table
        headClassName={s.tableHeadClass}
        getRowClassName={getRowClassName}
        rows={data}
        loading={loading}
        columns={columns}
        parentScrollRef={props.parentScrollRef}
        isRowExpandable={() => true}
        collapseOnRowClick={true}
        disableVirtualization
        skeletonModeLength={20}
        shouldRowExpand={(open, rowData) => {
          return (
            open && rowData && (open as any)?.origin_node_id === (rowData as any)?.origin_node_id
          );
        }}
        rowCollapsibleRender={(row: any) => {
          return (
            <PreviewFlowLazy
              lastRunId={row?.last_run_id}
              runId={row?.metrics?.run_id}
              originNodeId={row.origin_node_id}
            />
          );
        }}
      />

      {selectedCardID ? <FlowDetailViewContainerLazy insightsOnly /> : null}
    </>
  );
}

export { FlowRunsTable };
